import cx from 'classnames';
import { NavLink as RouterNavLink, useNavigate } from 'react-router-dom';

import styles from './NavLink.module.scss';
import type { NavLinkProps } from './NavLink.types';
import { WrapTextNodes } from '../../../utils/WrapTextNodes';
import useBuildVariantProps from '../../../../modules/theme/useBuildVariantProps';
import { TextProps } from '../../../atoms';
import { TextStylingProps } from '../../../atoms/Text/Text.types';

const NavContent = (props: Pick<NavLinkProps, 'children' | 'leadingIcon' | 'trailingIcon' | 'className'>) => {
  const { children, leadingIcon, trailingIcon, className } = props;

  return (
    <>
      {leadingIcon && (
        <div className={styles.leadingIcon}>
          {leadingIcon}
        </div>
      )}
      <WrapTextNodes as="span" className={className}>
        {children}
      </WrapTextNodes>
      {trailingIcon && (
        <div className={styles.trailingIcon}>
          {trailingIcon}
        </div>
      )}
    </>
  );
};

const NavigateNavLink = (
  props: Omit<NavLinkProps, 'to'> & {
    to: number,
    componentVariantProp: { 'component-variant': string },
    textClassName: string,
  },
) => {
  const navigate = useNavigate();
  const {
    to,
    children,
    className,
    leadingIcon,
    trailingIcon,
    componentVariantProp,
    textClassName,
    ...rest } = props;

  return (
    <a
      {...rest}
      onClick={() => navigate(to)}
      className={cx(styles.navLink, className)}
      {...componentVariantProp}
    >
      <NavContent
        children={children}
        leadingIcon={leadingIcon}
        trailingIcon={trailingIcon}
        className={textClassName}
      />
    </a>
  )
};

const NavLink = (props: NavLinkProps) => {
  const {
    to,
    children,
    className,
    'component-variant': componentVariant,
    appearance,
    size,
    style,
    type = 'Fill',
    leadingIcon,
    trailingIcon,
    ...rest
  } = props;

  /**
   * Check if the component should be rendered as a button.
   * If any of the appearance, size, style, or type props are provided, the component will be rendered as a button.
   */
  const asButton = [appearance, style, !['Fill', 'Underline'].includes(type)].some(Boolean);

  const buttonVariants: Record<string, string> = {
    appearance: appearance || 'Default',
    size: size || 'Small',
    style: style || 'Secondary',
    type: type || 'Filled',
  };

  const navLinkVariants: Record<string, string> = {
    type,
    size: size || 'Small',
  };

  const buildVariantOptions = {
    component: asButton ? 'button' : 'nav-link',
    // if the component is a button, we need to build the variant props
    // use the default values if some props are not provided
    variants: asButton ? buttonVariants : navLinkVariants,
    stateFlags: {
    },
    componentVariant,
  };

  const componentVariantProp = useBuildVariantProps(
    buildVariantOptions.component,
    buildVariantOptions.variants,
    buildVariantOptions.stateFlags,
    buildVariantOptions.componentVariant,
  );

  /**
   * If the component variant is provided, we don't need to add the additional class name.
   * Otherwise, we add the additional class name based on if the component is a button or a link.
   * This is to ensure that we use the correct theme variables for the component.
   */
  const asComponentVariant = componentVariant !== undefined;
  const additionalClassName = asComponentVariant ? '' : (asButton ? 'button' : 'link');

  const classNameProp = cx(
    styles.navLink,
    styles[additionalClassName],
    className,
  );

  if (typeof to === 'number') {
    return (
      <NavigateNavLink
        {...rest}
        to={to}
        children={children}
        className={classNameProp}
        componentVariantProp={componentVariantProp}
        textClassName={asButton ? styles.buttonText : styles.linkText}
      />
    )
  }

  const isExternal = /^https?:\/\//.test(to);
  if (isExternal) {
    return (
      <a
        {...rest}
        href={to}
        className={classNameProp}
        {
        ...componentVariantProp
        }
      >
        <NavContent
          children={children}
          leadingIcon={leadingIcon}
          trailingIcon={trailingIcon}
          className={asButton ? styles.buttonText : styles.linkText}
        />
      </a>
    );
  }

  return (
    <RouterNavLink
      {...rest}
      to={to}
      className={classNameProp}
      {
      ...componentVariantProp
      }
    >
      <NavContent
        children={children}
        leadingIcon={leadingIcon}
        trailingIcon={trailingIcon}
        className={asButton ? styles.buttonText : styles.linkText}
      />
    </RouterNavLink>
  );
};
export default NavLink;
